import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListCreator } from './useScorableItemFilters.d';
import { CheckboxState, getParentOfNode as getParentOfGroup, listCreatorFactory } from './selection-utils';
import { ScorableItemTableDataGroup, ScorableItemTableDataRow } from '../../per-student/hooks/useTableData';

interface DataToDetermineStateOfTableGroupBySubTree {
  newGroupStates: Record<string, CheckboxState>;
  newRowStates: Record<string, CheckboxState>;
}

interface Options {
  filteredTableDataGroups: ScorableItemTableDataGroup[];
  tableDataGroups: ScorableItemTableDataGroup[];
  setListCreator: Dispatch<SetStateAction<ListCreator | undefined>>;
  selectedItemIds?: string[];
}

export default function useScorableItemSelection({
  filteredTableDataGroups,
  tableDataGroups,
  setListCreator,
  selectedItemIds = [],
}: Options) {
  // key/val object containing checkbox state of table row
  const [rowStates, setRowStates] = useState<Record<string, CheckboxState>>({});
  const newRowStatesRef = useRef<typeof rowStates | null>(null);

  // key/val object containing checkbox state of table group
  const [groupStates, setGroupStates] = useState<Record<string, CheckboxState>>({});
  const newGroupStatesRef = useRef<typeof groupStates | null>(null);

  const flattenTableRows = useCallback((node: ScorableItemTableDataGroup): ScorableItemTableDataRow[] => {
    return [...(node.rows || []), ...(node.groups?.flatMap(flattenTableRows) || [])];
  }, []);

  const flattenTableGroups = useCallback((group: ScorableItemTableDataGroup): ScorableItemTableDataGroup[] => {
    return [group, ...(group.groups?.flatMap(flattenTableGroups) || [])];
  }, []);

  const allTableGroups = useMemo(() => {
    return tableDataGroups.flatMap(group => flattenTableGroups(group));
  }, [tableDataGroups, flattenTableGroups]);

  const allFilteredTableGroups = useMemo(() => {
    return filteredTableDataGroups.flatMap(group => flattenTableGroups(group));
  }, [filteredTableDataGroups, flattenTableGroups]);

  const allTableRows = useMemo(() => {
    return tableDataGroups.flatMap(node => flattenTableRows(node));
  }, [tableDataGroups, flattenTableRows]);

  const allFilteredTableRows = useMemo(() => {
    return filteredTableDataGroups.flatMap(node => flattenTableRows(node));
  }, [filteredTableDataGroups, flattenTableRows]);

  useEffect(() => {
    const rowsCheckedStates = allTableRows.reduce<Record<string, CheckboxState>>((acc, item) => {
      acc[item.id] = selectedItemIds?.includes(item.id) ? 'checked' : 'unchecked';

      return acc;
    }, {});

    const groupsCheckedStates = allTableGroups.reduce<Record<string, CheckboxState>>((acc, node) => {
      acc[node.id] = 'unchecked';

      return acc;
    }, {});

    setRowStates(rowsCheckedStates);
    setGroupStates(groupsCheckedStates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const determineStateOfTableGroupBySubTree = useCallback(
    (
      group: ScorableItemTableDataGroup,
      { newGroupStates, newRowStates }: DataToDetermineStateOfTableGroupBySubTree,
    ) => {
      let groupState: CheckboxState = 'indeterminate';

      if (!group.groups && group.rows) {
        const allRowsChecked = group.rows.every(item => newRowStates[item.id] === 'checked');
        const allRowsUnchecked = group.rows.every(item => newRowStates[item.id] === 'unchecked');

        if (allRowsChecked) {
          groupState = 'checked';
        } else if (allRowsUnchecked) {
          groupState = 'unchecked';
        }
      } else if (!group.rows && group.groups) {
        const allGroupsChecked = group.groups.every(childGroup => newGroupStates[childGroup.id] === 'checked');
        const allGroupsUnchecked = group.groups.every(childGroup => newGroupStates[childGroup.id] === 'unchecked');

        if (allGroupsChecked) {
          groupState = 'checked';
        } else if (allGroupsUnchecked) {
          groupState = 'unchecked';
        }
      } else if (group.groups && group.rows) {
        const allRowsChecked = group.rows.every(item => newRowStates[item.id] === 'checked');
        const allRowsUnchecked = group.rows.every(item => newRowStates[item.id] === 'unchecked');
        const allGroupsChecked = group.groups.every(childGroup => newGroupStates[childGroup.id] === 'checked');
        const allGroupsUnchecked = group.groups.every(childGroup => newGroupStates[childGroup.id] === 'unchecked');

        if (allRowsChecked && allGroupsChecked) {
          groupState = 'checked';
        } else if (allRowsUnchecked && allGroupsUnchecked) {
          groupState = 'unchecked';
        }
      }

      return groupState;
    },
    [],
  );

  useEffect(() => {
    setGroupStates(prev => {
      if (Object.keys(rowStates).length === 0) return prev;

      function flatMapNodesBottomUp(
        group: ScorableItemTableDataGroup,
        flatGroups: ScorableItemTableDataGroup[] = [],
      ): ScorableItemTableDataGroup[] {
        if (group.groups) {
          group.groups.forEach(childGroup => flatMapNodesBottomUp(childGroup, flatGroups));
        }

        flatGroups.push(group);

        return flatGroups;
      }

      const bottomUpGroupsInTree = filteredTableDataGroups.flatMap(group => flatMapNodesBottomUp(group));

      const newStates = bottomUpGroupsInTree.reduce<Record<string, CheckboxState>>(
        (acc, node) => {
          const newState = determineStateOfTableGroupBySubTree(node, {
            newGroupStates: acc,
            newRowStates: rowStates,
          });

          acc[node.id] = newState;

          return acc;
        },
        { ...prev },
      );

      return newStates;
    });
  }, [determineStateOfTableGroupBySubTree, filteredTableDataGroups, rowStates]);

  const findGroupInFilteredTree = useCallback(
    (id: string): ScorableItemTableDataGroup | null => {
      return allFilteredTableGroups.find(group => group.id === id) || null;
    },
    [allFilteredTableGroups],
  );

  const updateTableGroupAndChildren = useCallback((group: ScorableItemTableDataGroup, state: CheckboxState) => {
    if (!newRowStatesRef.current || !newGroupStatesRef.current) return;

    newGroupStatesRef.current[group.id] = state;

    (group.rows || []).forEach(item => {
      newRowStatesRef.current![item.id] = state;
    });

    group.groups?.forEach(childGroup => updateTableGroupAndChildren(childGroup, state));
  }, []);

  const determineStateOfGroupByRows = useCallback((rowStates: CheckboxState[]): CheckboxState => {
    const checkedCount = rowStates.filter(state => state === 'checked').length;
    const indeterminateCount = rowStates.filter(state => state === 'indeterminate').length;

    if (indeterminateCount > 0) return 'indeterminate';
    if (checkedCount === rowStates.length) return 'checked';
    if (checkedCount === 0) return 'unchecked';
    return 'indeterminate';
  }, []);

  const updateGroupChain = useCallback(
    (group: ScorableItemTableDataGroup) => {
      const rows = flattenTableRows(group);
      const states = rows
        .map(row => newRowStatesRef.current?.[row.id])
        .filter((state): state is CheckboxState => state !== undefined);

      if (states.length !== rows.length) return;

      const newGroupState = determineStateOfGroupByRows(states);

      if (!newGroupStatesRef.current || rowStates[group.id] === newGroupState) return;

      newGroupStatesRef.current[group.id] = newGroupState;
    },
    [determineStateOfGroupByRows, flattenTableRows, rowStates],
  );

  const updateParentChain = useCallback(
    (
      childGroup: ScorableItemTableDataGroup,
      { newGroupStates: newNodeStates, newRowStates: newScorableItemStates }: DataToDetermineStateOfTableGroupBySubTree,
    ) => {
      const parentGroup = getParentOfGroup(childGroup.id, allFilteredTableGroups);
      if (!parentGroup || !newGroupStatesRef.current) return;

      newGroupStatesRef.current[parentGroup.id] = determineStateOfTableGroupBySubTree(parentGroup, {
        newGroupStates: newNodeStates,
        newRowStates: newScorableItemStates,
      });

      updateParentChain(parentGroup, { newGroupStates: newNodeStates, newRowStates: newScorableItemStates });
    },
    [allFilteredTableGroups, determineStateOfTableGroupBySubTree],
  );

  // optimization possible by storing a key/value pair of the scorableItemId and its node object
  const getGroupOfRow = useCallback(
    (id: string): ScorableItemTableDataGroup | null => {
      const groupId = allFilteredTableRows.find(item => item.id === id)?.tableGroupId;
      if (!groupId) return null;
      return allFilteredTableGroups.find(node => node.id === groupId) || null;
    },
    [allFilteredTableGroups, allFilteredTableRows],
  );

  const setGroupState = useCallback(
    (id: string, state: CheckboxState) => {
      if (newRowStatesRef.current) return;

      const node = findGroupInFilteredTree(id);
      if (!node) return;

      newRowStatesRef.current = { ...rowStates };
      newGroupStatesRef.current = { ...groupStates };

      if (state !== 'indeterminate') {
        updateTableGroupAndChildren(node, state);
      }

      updateParentChain(node, {
        newGroupStates: newGroupStatesRef.current,
        newRowStates: newRowStatesRef.current,
      });

      setRowStates(newRowStatesRef.current);
      setGroupStates(newGroupStatesRef.current);
      const listCreator = listCreatorFactory({
        newRowStates: newRowStatesRef.current,
        allTableRows: allTableRows,
        allTableGroups: allTableGroups,
        filteredTableGroups: allFilteredTableGroups,
      });
      setListCreator(() => listCreator);

      newRowStatesRef.current = null;
      newGroupStatesRef.current = null;
    },
    [
      allFilteredTableGroups,
      allTableGroups,
      allTableRows,
      findGroupInFilteredTree,
      groupStates,
      rowStates,
      setListCreator,
      updateTableGroupAndChildren,
      updateParentChain,
    ],
  );

  const setScorableItemState = useCallback(
    (id: string, state: CheckboxState) => {
      if (newRowStatesRef.current || newGroupStatesRef.current) return;

      newRowStatesRef.current = { ...rowStates, [id]: state };

      const node = getGroupOfRow(id);
      if (!node) {
        newRowStatesRef.current = null;
        return;
      }

      newGroupStatesRef.current = { ...groupStates };

      updateGroupChain(node);
      updateParentChain(node, {
        newGroupStates: newGroupStatesRef.current,
        newRowStates: newRowStatesRef.current,
      });

      setRowStates(newRowStatesRef.current);
      setGroupStates(newGroupStatesRef.current);
      const listCreator = listCreatorFactory({
        newRowStates: newRowStatesRef.current,
        allTableGroups: allTableGroups,
        allTableRows: allTableRows,
        filteredTableGroups: allFilteredTableGroups,
      });
      setListCreator(() => listCreator);

      newRowStatesRef.current = null;
      newGroupStatesRef.current = null;
    },
    [
      allFilteredTableGroups,
      allTableGroups,
      allTableRows,
      getGroupOfRow,
      groupStates,
      rowStates,
      setListCreator,
      updateGroupChain,
      updateParentChain,
    ],
  );

  function selectAllItems() {
    newRowStatesRef.current = { ...rowStates };
    newGroupStatesRef.current = { ...groupStates };

    allFilteredTableRows.forEach(item => {
      newRowStatesRef.current![item.id] = 'checked';
    });
    allFilteredTableGroups.forEach(node => {
      newGroupStatesRef.current![node.id] = 'checked';
    });

    setRowStates(newRowStatesRef.current);
    setGroupStates(newGroupStatesRef.current);
    const listCreator = listCreatorFactory({
      newRowStates: newRowStatesRef.current,
      allTableGroups: allTableGroups,
      allTableRows: allTableRows,
      filteredTableGroups: allFilteredTableGroups,
    });
    setListCreator(() => listCreator);
    newRowStatesRef.current = null;
    newGroupStatesRef.current = null;
  }

  function clearAllItems() {
    newRowStatesRef.current = { ...rowStates };
    newGroupStatesRef.current = { ...groupStates };

    allFilteredTableRows.forEach(item => {
      newRowStatesRef.current![item.id] = 'unchecked';
    });

    allFilteredTableGroups.forEach(node => {
      newGroupStatesRef.current![node.id] = 'unchecked';
    });

    setRowStates(newRowStatesRef.current);
    setGroupStates(newGroupStatesRef.current);
    const listCreator = listCreatorFactory({
      newRowStates: newRowStatesRef.current,
      allTableGroups: allTableGroups,
      allTableRows: allTableRows,
      filteredTableGroups: allFilteredTableGroups,
    });
    setListCreator(() => listCreator);
    newRowStatesRef.current = null;
    newGroupStatesRef.current = null;
  }

  const getState = useCallback(
    (id: string): CheckboxState => {
      return rowStates[id] || groupStates[id] || 'unchecked';
    },
    [groupStates, rowStates],
  );

  const hasSelectedItems = useMemo(() => {
    return allFilteredTableGroups.some(node => groupStates[node.id] !== 'unchecked');
  }, [allFilteredTableGroups, groupStates]);

  return {
    result: Object.entries({ ...groupStates, ...rowStates })
      .filter(([, state]) => state === 'checked' || state === 'indeterminate')
      .map(([id]) => id),
    hasSelectedItems,
    getCheckboxState: getState,
    setNodeCheckboxState: setGroupState,
    setScorableItemCheckboxState: setScorableItemState,
    selectAllItems,
    clearAllItems,
  };
}
